import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ledgerApi } from "../lib/ledgerApi";
import HashString from "../components/HashString";
import { ChevronRight, Boxes } from "lucide-react";

export default function Blocks() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const limit = 25;

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await ledgerApi.listBlocks(limit + 1, skip);
        if (!alive) return;
        setHasMore(data.length > limit);
        setBlocks(data.slice(0, limit));
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const i = setInterval(load, 4000);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, [skip]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-10 md:py-14">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Blocks</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
            <Boxes className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
            Block Explorer
          </h1>
        </div>
        <div className="text-xs font-mono text-muted-foreground">{blocks.length} shown</div>
      </div>

      <div className="border border-border rounded-sm bg-card overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border bg-muted/30">
          <div className="col-span-2 sm:col-span-1">#</div>
          <div className="col-span-6 sm:col-span-5">Block hash</div>
          <div className="col-span-4 sm:col-span-3 hidden sm:block">Merkle root</div>
          <div className="col-span-3 sm:col-span-2">Txns</div>
          <div className="col-span-1 sm:col-span-1 text-right">→</div>
        </div>
        {loading && blocks.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        )}
        {blocks.map((b) => (
          <Link
            key={b.block_number}
            to={`/blocks/${b.block_number}`}
            data-testid={`block-list-row-${b.block_number}`}
            className="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-muted/40 transition-colors border-b border-border last:border-b-0"
          >
            <div className="col-span-2 sm:col-span-1 font-mono text-sm">#{b.block_number}</div>
            <div className="col-span-6 sm:col-span-5 min-w-0"><HashString value={b.block_hash} label="Block hash" /></div>
            <div className="col-span-4 sm:col-span-3 hidden sm:block min-w-0"><HashString value={b.merkle_root} label="Merkle root" /></div>
            <div className="col-span-3 sm:col-span-2 font-mono text-sm">{b.tx_count}</div>
            <div className="col-span-1 sm:col-span-1 text-right text-muted-foreground"><ChevronRight className="w-4 h-4 inline" strokeWidth={1.5} /></div>
          </Link>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2">
        <button
          data-testid="blocks-prev"
          disabled={skip === 0}
          onClick={() => setSkip(Math.max(0, skip - limit))}
          className="px-3 py-1.5 border border-border rounded-sm text-xs hover:border-foreground disabled:opacity-40 transition-colors"
        >
          ← Newer
        </button>
        <div className="text-xs font-mono text-muted-foreground">offset {skip}</div>
        <button
          data-testid="blocks-next"
          disabled={!hasMore}
          onClick={() => setSkip(skip + limit)}
          className="px-3 py-1.5 border border-border rounded-sm text-xs hover:border-foreground disabled:opacity-40 transition-colors"
        >
          Older →
        </button>
      </div>
    </div>
  );
}
